/**
 * Every user-facing string in the product.
 *
 * The voice is a sharp consultant who likes you and bills by the hour: second person,
 * verbs, short sentences, allowed to be a little blunt, never apologising twice. No
 * exclamation marks, no emoji, nothing that names the machinery. docs/DESIGN.md 8 is
 * the binding version of that rule and it applies to both languages.
 *
 * The Russian is written, not translated word for word. Register is what has to match:
 * a Russian string that reads like a manual has failed even if it means the same thing.
 *
 * A key that nothing renders is a lie about the product, so this file holds only
 * strings something on screen actually uses. Four were removed rather than wired up
 * (`bargeInHint`, `idlePrompt`, `exportDone`, `exportFailed`): the first two had no
 * moment that was theirs, and the last two said "copied" about a download.
 */

import type { Locale } from "../../../shared/types";

export interface Copy {
  // The landing, before anyone has signed in. One screen, nothing scrolls.
  landingName: string;
  landingWhat: string;
  landingLeave: string;
  /** The one quiet sentence shown when the door is shut. */
  landingPrivate: string;
  landingCta: string;
  /** The second way in, for somebody who is not here for the first time. */
  landingSignIn: string;

  // The one honest moment a brand-new account gets before the interview starts.
  // One screen, three sentences, and a way past it — DESIGN.md 8: this is a
  // consultant saying what the next quarter of an hour is, not a product tour.
  welcomeTitle: string;
  welcomeWhat: string;
  welcomeHow: string;
  welcomeStop: string;
  welcomeCta: string;
  welcomeSkip: string;

  // Cold start, before anything has been said.
  emptyHero: string;
  emptyHint: string;

  // The mono status line. One fixed place, every transient state in the product.
  listening: string;
  listeningLong: string;
  thinking: string;
  speaking: string;

  // Failure.
  errorGeneric: string;
  errorNetwork: string;
  errorRecovered: string;
  /** The way back out of a turn that did not land. */
  retryCta: string;

  // Microphone.
  micAsk: string;
  micDenied: string;
  micBusy: string;
  /** The key. Hold-to-talk is real on SPACE and nowhere else. */
  micLabel: string;
  /** The button. A click starts and a second click stops, so it may not say "hold". */
  micButton: string;

  // Voice degraded. The conversation is unaffected, which is what this has to convey.
  voiceUnavailable: string;

  // Continuity between sessions.
  sessionResumed: string;
  sessionNew: string;
  /** The conversation's language is fixed at its first word; the chrome's is not. */
  languageLocked: string;

  // The dock.
  inputPlaceholder: string;
  hintTalk: string;
  hintSend: string;
  hintRelease: string;
  hintThinking: string;

  /**
   * The chips above the dock. Only their frame is written here — every chip's text
   * comes from the server in the language the conversation is being held in.
   */
  suggestionsLabel: string;

  // The case switcher. One business being advised; the consultant runs several.
  caseCurrent: string;
  caseListLabel: string;
  caseUntitled: string;
  caseOpen: string;
  caseNewCta: string;
  caseRenameCta: string;
  caseNameLabel: string;
  caseNamePlaceholder: string;
  /** Russian needs three forms, so this cannot be a template. */
  caseConversationCount: (n: number) => string;
  /** Written while a case is opening. The name is the point: it says which one. */
  caseOpening: (name: string) => string;
  caseSwitchFailed: string;
  caseCreateFailed: string;
  caseRenameFailed: string;

  // The conversations inside a case.
  conversationListLabel: string;
  conversationUntitled: string;
  conversationNewCta: string;
  conversationOpenFailed: string;

  // Intake: the consultant getting acquainted with a new case.
  /** The quiet marker on the stage while the conversation is still an intake. */
  intakeLabel: string;
  /** Said once, in the status line, the moment the profile is complete. */
  intakeDone: string;
  /** The case-menu action that starts a fresh intake for this case. */
  intakeRedoCta: string;
  /** The words for a respondent's role, for the one-liner in the case menu. */
  respondentRole: Record<"founder" | "employee" | "executive" | "explorer" | "other", string>;

  // Documents attached to the case.
  documentsLabel: string;
  documentAttachCta: string;
  /** The status line while a file is being read and summarised. */
  documentReading: string;
  documentUploadFailed: string;
  documentDeleteConfirm: string;

  // Notes.
  notesHeader: string;
  notesCaveat: string;
  notesEmpty: string;
  /** The rail's count badge. Russian needs three forms, so this cannot be a template. */
  notesCount: (n: number) => string;
  /** Kind and confidence are words, never colours or bars. */
  findingKind: Record<"fact" | "problem" | "hypothesis" | "recommendation" | "question", string>;
  confidenceMaybe: string;
  confidenceLikely: string;

  // Asking the collected data a question. A text surface, opened from the rail —
  // the stage stays a voice conversation and nothing here belongs in the ear.
  /** The quiet line in the rail that opens it. Never a button that shouts. */
  askCta: string;
  askTitle: string;
  /** The promise, in one line: it answers from the record or it says it cannot. */
  askSub: string;
  askPlaceholder: string;
  askSend: string;
  askClose: string;
  askClear: string;
  /** Shown once, before the first question, instead of an empty panel. */
  askEmpty: string;
  /** The status line while an answer is being read out of the record. */
  askThinking: string;
  askFailed: string;
  /** Heads the list of rows the answer cited. */
  askSourcesLabel: string;
  /** What each kind of cited row is called, in a founder's words. */
  askSourceKind: Record<"finding" | "message" | "document" | "theme" | "quote", string>;
  /**
   * A note whose conversation was deleted. The note stays — it is still something
   * that was said — but the transcript it came out of is gone, so it can never be
   * traced back, and the rail says so on the note rather than in a footnote.
   */
  notesSourceless: string;

  // Export.
  exportCta: string;
  /** The same notes as CSV, for whoever lives in a spreadsheet. */
  exportCsvCta: string;
  exportSub: string;

  // The transcript. Typeface tells the speakers apart on screen; this is for the ear.
  speakerThem: string;
  speakerYou: string;

  // Chrome.
  /** Names the action, not the state: a bare "Light" could be either. */
  themeToLight: string;
  themeToDark: string;

  // Auth.
  signInTitle: string;
  signInSub: string;
  signInCta: string;
  signInSwitch: string;
  signUpTitle: string;
  signUpSub: string;
  signUpCta: string;
  signUpSwitch: string;
  emailLabel: string;
  passwordLabel: string;
  /** The rule, stated before it is broken rather than after. */
  passwordRule: string;
  /** The eye. Labelled by what pressing it does, never by what it currently is. */
  passwordShow: string;
  passwordHide: string;
  /** Out of the form and back to the landing. */
  authBack: string;
  /**
   * What the form itself refuses, before anything is sent. Each one names the
   * field it is about, because "invalid input" is not a sentence that helps.
   */
  errEmailRequired: string;
  errEmailInvalid: string;
  errPasswordRequired: string;
  errPasswordShort: string;
  signOutCta: string;
  signOut: string;
  // -------------------------------------------------------------------------
  // Studies: one question, N respondents, one synthesis.
  //
  // The register is the same consultant. A study screen that congratulates you on
  // reaching saturation is two products fighting, so nothing here celebrates and
  // nothing here rounds. Counts are always "7 of 11" and never a percentage:
  // 18% of nine people is a lie (PLATFORM-W4.md 1.5).
  // -------------------------------------------------------------------------

  /** The one count format the whole feature uses. Set in the mono face, always. */
  ofN: (a: number, b: number) => string;

  studiesLabel: string;
  studiesCount: (n: number) => string;
  studiesTitle: string;
  studiesSub: string;
  studiesEmpty: string;
  /** Out of the studies view and back to talking to the consultant. */
  studiesBack: string;
  studiesListFailed: string;
  studyNewCta: string;
  studyUntitled: string;
  studyOpenFailed: string;

  // Making one. Four decisions and a spine, on one screen, under two minutes.
  studyCreateTitle: string;
  studyCreateSub: string;
  studyGoalLabel: string;
  studyGoalPlaceholder: string;
  studyGoalHint: string;
  studyGoalRequired: string;
  studyAudienceLabel: string;
  studyAudience: Record<"customer" | "employee" | "friendly", string>;
  studyAudienceHint: Record<"customer" | "employee" | "friendly", string>;
  studyTimeLabel: string;
  /** The stated budget is a contract, and the interviewer holds it as a governor. */
  studyTimeHint: string;
  studyMinutes: (n: number) => string;
  studyTargetLabel: string;
  studyTargetHint: string;
  studySpineLabel: string;
  studySpineHint: string;
  studySpineDraftCta: string;
  studySpineDrafting: string;
  studySpineDraftFailed: string;
  studyTopicTitlePlaceholder: string;
  studyTopicIntentPlaceholder: string;
  studyTopicAddCta: string;
  studyTopicRemoveCta: string;
  studyHypothesesLabel: string;
  studyHypothesesHint: string;
  studyHypothesisPlaceholder: string;
  studyHypothesisAddCta: string;
  studyCreateCta: string;
  studyCreateFailed: string;
  studyCreating: string;
  studyCancelCta: string;

  // The link. Minted once, readable once, and the copy says so.
  studyLinkLabel: string;
  studyLinkOnce: string;
  studyLinkCopyCta: string;
  studyLinkCopied: string;
  studyLinkCopyFailed: string;
  studyLinkMakeCta: string;
  studyLinkNewCta: string;
  studyLinkFailed: string;
  studyLinkExpires: (when: string) => string;
  studyLinkNone: string;
  studyLinksLabel: string;
  studyLinkKind: Record<"open" | "personal", string>;
  studyLinkUsed: string;
  studyLinkRevokeCta: string;
  studyLinkRevoked: string;
  studyLinkExpired: string;
  studyLinkRevokeFailed: string;

  // The board.
  studyGoalHeader: string;
  studyStatus: Record<"draft" | "fielding" | "closed", string>;
  studyProgressSub: string;
  studyMetaAudience: string;
  studyMetaTime: string;
  studyParticipantsLabel: string;
  studyParticipantsEmpty: string;
  studyAnonymous: string;
  participantStatus: Record<
    | "invited"
    | "screened_out"
    | "over_quota"
    | "started"
    | "completed"
    | "abandoned"
    | "disqualified",
    string
  >;
  studyTurnCount: (n: number) => string;
  studyDuration: (ms: number) => string;
  studyDurationShort: string;
  studyReadCta: string;
  studyHideCta: string;
  studyTranscriptFailed: string;
  studyCloseCta: string;
  studyUpdateFailed: string;
  /** Whether there is enough on the board to be worth a run. Never a green tick. */
  studyReady: Record<"too_few" | "enough" | "saturating" | "closed", string>;

  // The synthesis: the payoff screen.
  synthesisLabel: string;
  synthesisNone: string;
  synthesisRunCta: string;
  synthesisRunAgainCta: string;
  synthesisCost: string;
  synthesisRunning: (done: number, total: number) => string;
  synthesisRunningVague: string;
  synthesisFailed: string;
  synthesisFrom: (n: number, when: string) => string;
  synthesisAnswerLabel: string;
  synthesisThemesLabel: string;
  synthesisAskedOf: string;
  synthesisBand: Record<"strong" | "provisional" | "signal", string>;
  synthesisDirectness: Record<"behaviour" | "opinion" | "hypothetical", string>;
  synthesisPrimed: string;
  synthesisAgainst: string;
  synthesisEvidenceCta: string;
  synthesisEvidenceHideCta: string;
  synthesisSaidDidLabel: string;
  synthesisSaidDidHint: string;
  synthesisSaid: string;
  synthesisDid: string;
  synthesisDisagreementsLabel: string;
  synthesisDisagreementsHint: string;
  synthesisFor: string;
  synthesisWithheld: (n: number) => string;
  /** Themes the priming check disqualified. Shown in full, counted nowhere. */
  synthesisUncountedLabel: string;
  synthesisUncountedHint: string;
  /** Stands where the count would be on such a theme. Never a number. */
  synthesisUncountedMark: string;
  synthesisSinglesLabel: string;
  synthesisSinglesHint: string;
  synthesisHypothesesLabel: string;
  synthesisVerdict: Record<"supported" | "contradicted" | "untested" | "refined", string>;
  /** Employee studies never report a theme below the threshold, and say so. */
  synthesisThreshold: (n: number) => string;
  synthesisSaturationLabel: string;
  synthesisSaturationEarly: (n: number) => string;
  synthesisSaturationAt: (n: number, pct: number) => string;
  synthesisSaturationFirm: (n: number) => string;
  synthesisSaturationNone: string;
  synthesisNextLabel: string;
  synthesisArtifactsLabel: string;
  synthesisArtifact: Record<"brief" | "docx" | "csv" | "book" | "page", string>;
  synthesisArtifactsSub: string;


  // Destructive.
  deleteCta: string;
  deleteConfirm: string;
  deleteYes: string;
  deleteNo: string;
  deleteFailed: string;
}

const en: Copy = {
  landingName: "Consultant",
  landingWhat:
    "A session is a real customer development interview about your business. Spoken, direct, and about twenty minutes.",
  landingLeave: "You leave with structured notes and a short brief worth acting on.",
  landingPrivate: "Access is limited for now.",
  landingCta: "Begin",
  landingSignIn: "I already have an account",

  welcomeTitle: "Before we start.",
  welcomeWhat:
    "I run this like a consultant would: I ask about your business, you answer, and I write down what matters as we go.",
  welcomeHow:
    "About fifteen minutes. Talk out loud or type \u2014 both work, and you can switch whenever.",
  welcomeStop: "Stop any time. Everything said is saved, and the notes are yours to take.",
  welcomeCta: "Start",
  welcomeSkip: "Skip this",

  emptyHero: "So. What are you building?",
  emptyHint: "Talk or type. Twenty minutes gets us somewhere useful.",

  listening: "Listening",
  listeningLong: "Take your time.",
  thinking: "Thinking",
  speaking: "Speaking",

  errorGeneric: "That did not go through. Try again, and I will keep what you already said.",
  errorNetwork: "Connection dropped. Your notes are saved. I will pick up where we stopped.",
  errorRecovered: "Back. You were saying.",
  retryCta: "Say that again",

  micAsk: "I will need the microphone to hear you. Nothing is recorded until you start talking.",
  micDenied:
    "Your browser is holding the microphone back. Allow it from the address bar, or just type. Either works.",
  micBusy: "Something else has the microphone. Close it and I will try again.",
  micLabel: "Hold to talk",
  micButton: "Talk",

  voiceUnavailable: "No voice right now. We keep going in text.",

  sessionResumed: "We stopped mid conversation. Keep going, or has something changed?",
  sessionNew: "New conversation. I still remember the business.",
  languageLocked: "This one stays in the language it started in. Start a new conversation and I switch.",

  inputPlaceholder: "Start anywhere",
  hintTalk: "SPACE TO TALK",
  hintSend: "RETURN TO SEND",
  hintRelease: "RELEASE TO SEND",
  hintThinking: "THINKING",

  suggestionsLabel: "Things you could say next",

  caseCurrent: "Current case",
  caseListLabel: "Your cases",
  caseUntitled: "Untitled case",
  caseOpen: "Open",
  caseNewCta: "New case",
  caseRenameCta: "Rename",
  caseNameLabel: "Case name",
  caseNamePlaceholder: "Name this one",
  caseConversationCount: (n) => (n === 1 ? "1 conversation" : `${n} conversations`),
  caseOpening: (name) => `Opening ${name}`,
  caseSwitchFailed: "That case did not open. You are still on the last one.",
  caseCreateFailed: "The case was not created. Try that again.",
  caseRenameFailed: "The name did not save. Try that again.",

  conversationListLabel: "Conversations in this case",
  conversationUntitled: "Untitled conversation",
  conversationNewCta: "New conversation",
  conversationOpenFailed: "That conversation did not open. You are still where you were.",

  intakeLabel: "Getting acquainted",
  intakeDone: "Good. Now I know who I am talking to.",
  intakeRedoCta: "Redo the intro",
  respondentRole: {
    founder: "Founder",
    employee: "Employee",
    executive: "Executive",
    explorer: "Exploring",
    other: "Other",
  },

  documentsLabel: "Documents",
  documentAttachCta: "Attach a document",
  documentReading: "Reading the file",
  documentUploadFailed: "The file did not go through. Try again.",
  documentDeleteConfirm: "Remove this document?",

  notesHeader: "What I have so far",
  notesCaveat: "Rough, and it will change.",
  notesEmpty: "Nothing worth writing down yet. Keep talking.",
  notesCount: (n) => (n === 1 ? "1 note" : `${n} notes`),
  findingKind: {
    fact: "Fact",
    problem: "Problem",
    hypothesis: "Hypothesis",
    recommendation: "Recommendation",
    question: "Question",
  },
  confidenceMaybe: "Maybe",
  confidenceLikely: "Likely",
  notesSourceless: "Source deleted",

  askCta: "Ask this all a question",
  askTitle: "Ask the record",
  askSub: "Answered out of what is here, or not answered. I will not fill a gap with something plausible.",
  askPlaceholder: "What did people actually say about the price?",
  askSend: "Ask",
  askClose: "Close",
  askClear: "Start over",
  askEmpty:
    "Everything you have said, everything I wrote down, the files you handed me and whatever the studies found. Ask it something.",
  askThinking: "Reading back through it",
  askFailed: "That did not come back. Ask it again.",
  askSourcesLabel: "Out of",
  askSourceKind: {
    finding: "note",
    message: "what was said",
    document: "file",
    theme: "study finding",
    quote: "quote",
  },

  exportCta: "Take the notes",
  exportCsvCta: "The notes as a table",
  exportSub: "Everything we covered, on one page.",

  speakerThem: "Consultant",
  speakerYou: "You",

  themeToLight: "Switch to light",
  themeToDark: "Switch to dark",

  signInTitle: "Welcome back.",
  signInSub: "Sign in and I will pull up where we left off.",
  signInCta: "Continue",
  signInSwitch: "Already have an account? Sign in",
  signUpTitle: "Make an account so I remember you next time.",
  signUpSub: "An email and a password. Nothing else, and no card.",
  signUpCta: "Create account",
  signUpSwitch: "New here? Create an account",
  emailLabel: "Email",
  passwordLabel: "Password",
  passwordRule: "At least 8 characters.",
  passwordShow: "Show password",
  passwordHide: "Hide password",
  authBack: "Back",
  errEmailRequired: "Enter your email address.",
  errEmailInvalid: "That does not look like an email address.",
  errPasswordRequired: "Enter your password.",
  errPasswordShort: "A password needs at least 8 characters.",
  signOutCta: "Sign out",
  signOut: "Signing out. Your notes stay.",
  ofN: (a, b) => `${a} of ${b}`,

  studiesLabel: "Studies",
  studiesCount: (n) => (n === 1 ? "1 study" : `${n} studies`),
  studiesTitle: "Ask other people.",
  studiesSub: "One question, several people, one answer you can defend.",
  studiesEmpty: "No studies yet. Write the question and you get a link to send.",
  studiesBack: "Back to the conversation",
  studiesListFailed: "The studies did not load. Try again.",
  studyNewCta: "New study",
  studyUntitled: "Untitled study",
  studyOpenFailed: "That study did not open. You are still where you were.",

  studyCreateTitle: "What do you want answered?",
  studyCreateSub: "Four decisions and a topic spine. Two minutes, and you have a link.",
  studyGoalLabel: "The question",
  studyGoalPlaceholder: "Why do people stop paying in month three?",
  studyGoalHint: "Yours, not theirs. Nobody you interview ever sees this sentence.",
  studyGoalRequired: "Write the question first.",
  studyAudienceLabel: "Who you are asking",
  studyAudience: {
    customer: "Customers",
    employee: "Your team",
    friendly: "People who will say it straight",
  },
  studyAudienceHint: {
    customer: "Discovery. Ask about their life, never about your idea.",
    employee: "Internal diagnosis. Reported by group, never by one person.",
    friendly: "A hypothesis test with people who will not spare you.",
  },
  studyTimeLabel: "How much of their time is fair",
  studyTimeHint: "A promise, not an estimate. The interview closes when it runs out.",
  studyMinutes: (n) => `${n} min`,
  studyTargetLabel: "How many people",
  studyTargetHint:
    "Eight is usually enough to see a pattern. Under five, treat everything as signal.",
  studySpineLabel: "What every interview has to cover",
  studySpineHint:
    "Three to six topics. Not questions: the order changes as the person talks.",
  studySpineDraftCta: "Draft it from the question",
  studySpineDrafting: "Drafting the spine",
  studySpineDraftFailed: "The draft did not come back. Write the topics yourself, or try again.",
  studyTopicTitlePlaceholder: "Topic",
  studyTopicIntentPlaceholder: "What you need to come away knowing",
  studyTopicAddCta: "Add a topic",
  studyTopicRemoveCta: "Remove",
  studyHypothesesLabel: "What you already believe",
  studyHypothesesHint: "Optional, and useful. The synthesis reports which of these survived.",
  studyHypothesisPlaceholder: "They leave because setting it up takes a week",
  studyHypothesisAddCta: "Add a belief",
  studyCreateCta: "Create the study and a link",
  studyCreateFailed: "The study was not created. Try that again.",
  studyCreating: "Setting up the study",
  studyCancelCta: "Cancel",

  studyLinkLabel: "The link",
  studyLinkOnce:
    "This is the only time it is shown. It is not stored anywhere. Lose it and you make another.",
  studyLinkCopyCta: "Copy the link",
  studyLinkCopied: "Copied. Send it before you close this.",
  studyLinkCopyFailed: "The clipboard refused. Select the link and copy it by hand.",
  studyLinkMakeCta: "Make a link",
  studyLinkNewCta: "Make another link",
  studyLinkFailed: "The link was not created. Try that again.",
  studyLinkExpires: (when) => `Good until ${when}`,
  studyLinkNone: "No link yet. Make one and send it.",
  studyLinksLabel: "Links you have made",
  studyLinkKind: { open: "Open link", personal: "Personal link" },
  studyLinkUsed: "used",
  studyLinkRevokeCta: "Revoke",
  studyLinkRevoked: "Revoked",
  studyLinkExpired: "Expired",
  studyLinkRevokeFailed: "That link was not revoked. Try again.",

  studyGoalHeader: "The question",
  studyStatus: { draft: "Draft", fielding: "Fielding", closed: "Closed" },
  studyProgressSub: "interviews finished",
  studyMetaAudience: "Asking",
  studyMetaTime: "Each",
  studyParticipantsLabel: "Who has answered",
  studyParticipantsEmpty: "Nobody yet. The board fills as they land.",
  studyAnonymous: "Anonymous",
  participantStatus: {
    invited: "Invited",
    screened_out: "Not a fit",
    over_quota: "Group full",
    started: "Talking",
    completed: "Finished",
    abandoned: "Stopped",
    disqualified: "Thrown out",
  },
  studyTurnCount: (n) => (n === 1 ? "1 turn" : `${n} turns`),
  studyDuration: (ms) => `${Math.max(1, Math.round(ms / 60000))} min`,
  studyDurationShort: "under a minute",
  studyReadCta: "Read it",
  studyHideCta: "Close",
  studyTranscriptFailed: "That transcript did not open. Try again.",
  studyCloseCta: "Close the study",
  studyUpdateFailed: "That did not save. Try again.",
  studyReady: {
    too_few: "Too few to claim a pattern. Keep sending the link.",
    enough: "Enough here to be worth a run.",
    saturating: "The last interviews are adding little. This is close to done.",
    closed: "Closed. Nothing more is coming in.",
  },

  synthesisLabel: "The synthesis",
  synthesisNone: "Nothing synthesised yet.",
  synthesisRunCta: "Synthesise",
  synthesisRunAgainCta: "Run it again",
  synthesisCost:
    "A run reads every interview and costs real money, so it happens when you say so and not before.",
  synthesisRunning: (done, total) => `Reading interview ${done} of ${total}`,
  synthesisRunningVague: "Reading the interviews",
  synthesisFailed: "The run did not finish. Nothing was lost. Try again.",
  synthesisFrom: (n, when) => (n === 1 ? `1 interview, ${when}` : `${n} interviews, ${when}`),
  synthesisAnswerLabel: "The answer",
  synthesisThemesLabel: "Themes",
  synthesisAskedOf: "of the people it came up with",
  synthesisBand: { strong: "Strong", provisional: "Provisional", signal: "Signal" },
  synthesisDirectness: {
    behaviour: "Behaviour",
    opinion: "Opinion",
    hypothetical: "Hypothetical",
  },
  synthesisPrimed: "Interviewer said it first",
  synthesisAgainst: "Against",
  synthesisEvidenceCta: "The words",
  synthesisEvidenceHideCta: "Hide the words",
  synthesisSaidDidLabel: "Said / did",
  synthesisSaidDidHint:
    "The same person predicting one thing and having already done another.",
  synthesisSaid: "Said",
  synthesisDid: "Did",
  synthesisDisagreementsLabel: "Where they disagree",
  synthesisDisagreementsHint:
    "Left unresolved. The disagreement is where the work is, so it is not averaged away.",
  synthesisFor: "For",
  synthesisWithheld: (n) =>
    n === 1
      ? "One theme is held back: too few people said it to report by group."
      : `${n} themes are held back: too few people said them to report by group.`,
  synthesisUncountedLabel: "Our words, not theirs",
  synthesisUncountedHint:
    "Most of what holds these up is somebody answering a word the interviewer used " +
    "first. Counting them would count how often the question got asked, so they are " +
    "left out of the answer and printed here in full instead.",
  synthesisUncountedMark: "Interviewer's own word",
  synthesisSinglesLabel: "One person said this",
  synthesisSinglesHint: "Worth knowing. Not counted, because one person is never a pattern.",
  synthesisHypothesesLabel: "What you believed going in",
  synthesisVerdict: {
    supported: "Supported",
    contradicted: "Contradicted",
    untested: "Untested",
    refined: "Became something else",
  },
  synthesisThreshold: (n) =>
    `Reported by group only. Nothing fewer than ${n} people said appears here.`,
  synthesisSaturationLabel: "Saturation",
  synthesisSaturationEarly: (n) =>
    `${n} interviews. Too few to call. Treat everything above as signal.`,
  synthesisSaturationAt: (n, pct) =>
    `The three interviews ending at ${n} added ${pct}% new codes. That is the conventional saturation point. Two more would make it firm.`,
  synthesisSaturationFirm: (n) =>
    `By interview ${n}, three in a row added nothing new. That is as firm as this method gets.`,
  synthesisSaturationNone: "New material is still arriving with every interview. Keep going.",
  synthesisNextLabel: "What to ask next",
  synthesisArtifactsLabel: "Take it with you",
  synthesisArtifact: {
    brief: "The brief",
    docx: "The brief as a document",
    csv: "Every quote as a table",
    book: "The same table, as a workbook",
    page: "A page you can send",
  },
  synthesisArtifactsSub:
    "Every claim names how many people said it, out of how many were asked.",


  deleteCta: "Delete",
  deleteConfirm: "Delete this conversation? The notes go with it.",
  deleteYes: "Delete",
  deleteNo: "Keep it",
  deleteFailed: "That did not delete. Try again.",
};

const ru: Copy = {
  landingName: "Консультант",
  landingWhat:
    "Сессия — это настоящее кастдев-интервью о вашем бизнесе. Вслух, по делу, минут двадцать.",
  landingLeave: "На выходе — структурированные записи и короткая выжимка, с которой можно работать.",
  landingPrivate: "Доступ пока ограничен.",
  landingCta: "Начать",
  landingSignIn: "У меня уже есть аккаунт",

  welcomeTitle: "Перед началом.",
  welcomeWhat:
    "Работаем как с консультантом: я спрашиваю про ваш бизнес, вы отвечаете, а я по ходу записываю важное.",
  welcomeHow:
    "Минут пятнадцать. Говорите вслух или пишите — работает и так, и так, и переключаться можно в любой момент.",
  welcomeStop:
    "Остановиться можно когда угодно. Сказанное сохраняется, записи остаются у вас.",
  welcomeCta: "Начать",
  welcomeSkip: "Пропустить",

  emptyHero: "Итак. Что за бизнес?",
  emptyHint: "Говорите или пишите. За двадцать минут дойдём до чего-то полезного.",

  listening: "Слушаю",
  listeningLong: "Не торопитесь.",
  thinking: "Думаю",
  speaking: "Говорю",

  errorGeneric: "Не прошло. Попробуйте ещё раз, сказанное я сохраню.",
  errorNetwork: "Связь оборвалась. Записи сохранены. Продолжим с того места, где остановились.",
  errorRecovered: "Связь вернулась. Продолжайте.",
  retryCta: "Повторить",

  micAsk: "Чтобы вас слышать, мне нужен микрофон. Запись начнётся, только когда вы заговорите.",
  micDenied:
    "Браузер не пускает к микрофону. Разрешите его в адресной строке или просто пишите. Оба способа рабочие.",
  micBusy: "Микрофон занят другой программой. Закройте её, и я попробую снова.",
  micLabel: "Держите, чтобы говорить",
  micButton: "Говорить",

  voiceUnavailable: "Голос сейчас недоступен. Продолжаем текстом.",

  sessionResumed: "Мы не договорили в прошлый раз. Продолжим оттуда, или что-то изменилось?",
  sessionNew: "Новый разговор. Бизнес ваш я помню.",
  languageLocked: "Этот разговор останется на языке, на котором начался. Начните новый — и я перейду.",

  inputPlaceholder: "Начните с чего угодно",
  hintTalk: "ПРОБЕЛ ДЛЯ ГОЛОСА",
  hintSend: "ENTER ДЛЯ ОТПРАВКИ",
  hintRelease: "ОТПУСТИТЕ ДЛЯ ОТПРАВКИ",
  hintThinking: "ДУМАЮ",

  suggestionsLabel: "Что можно сказать дальше",

  caseCurrent: "Сейчас разбираем",
  caseListLabel: "Ваши бизнесы",
  caseUntitled: "Без названия",
  caseOpen: "Открыт",
  caseNewCta: "Другой бизнес",
  caseRenameCta: "Переименовать",
  caseNameLabel: "Название бизнеса",
  caseNamePlaceholder: "Назовите его",
  caseConversationCount: (n) => `${n} ${pluralRu(n, "разговор", "разговора", "разговоров")}`,
  caseOpening: (name) => `Открываю ${name}`,
  caseSwitchFailed: "Этот бизнес не открылся. Остаёмся на прежнем.",
  caseCreateFailed: "Не получилось завести новый. Попробуйте ещё раз.",
  caseRenameFailed: "Название не сохранилось. Попробуйте ещё раз.",

  conversationListLabel: "Разговоры об этом бизнесе",
  conversationUntitled: "Без названия",
  conversationNewCta: "Новый разговор",
  conversationOpenFailed: "Этот разговор не открылся. Остаёмся на прежнем.",

  intakeLabel: "Знакомство",
  intakeDone: "Хорошо. Теперь я понимаю, с кем говорю.",
  intakeRedoCta: "Познакомиться заново",
  respondentRole: {
    founder: "Основатель",
    employee: "Сотрудник",
    executive: "Руководитель",
    explorer: "Присматривается",
    other: "Другое",
  },

  documentsLabel: "Документы",
  documentAttachCta: "Приложить документ",
  documentReading: "Читаю файл",
  documentUploadFailed: "Файл не загрузился. Попробуйте ещё раз.",
  documentDeleteConfirm: "Убрать этот документ?",

  notesHeader: "Что у меня пока есть",
  notesCaveat: "Начерно, и это ещё изменится.",
  notesEmpty: "Записывать пока нечего. Рассказывайте дальше.",
  notesCount: (n) => `${n} ${pluralRu(n, "заметка", "заметки", "заметок")}`,
  findingKind: {
    fact: "Факт",
    problem: "Проблема",
    hypothesis: "Гипотеза",
    recommendation: "Рекомендация",
    question: "Вопрос",
  },
  confidenceMaybe: "Возможно",
  confidenceLikely: "Скорее да",
  notesSourceless: "Разговор удалён",

  askCta: "Спросить по всему этому",
  askTitle: "Спросить записи",
  askSub: "Отвечаю тем, что здесь есть, либо не отвечаю. Правдоподобным пересказом дырку не закрою.",
  askPlaceholder: "Что люди на самом деле говорили про цену?",
  askSend: "Спросить",
  askClose: "Закрыть",
  askClear: "Начать заново",
  askEmpty:
    "Всё, что вы рассказали, всё, что я записал, файлы, которые вы дали, и то, что нашли исследования. Спрашивайте.",
  askThinking: "Перечитываю",
  askFailed: "Ответ не пришёл. Спросите ещё раз.",
  askSourcesLabel: "Из чего",
  askSourceKind: {
    finding: "заметка",
    message: "из разговора",
    document: "файл",
    theme: "вывод исследования",
    quote: "цитата",
  },

  exportCta: "Забрать записи",
  exportCsvCta: "Записи таблицей",
  exportSub: "Всё, что мы разобрали, на одной странице.",

  speakerThem: "Консультант",
  speakerYou: "Вы",

  themeToLight: "Светлая тема",
  themeToDark: "Тёмная тема",

  signInTitle: "С возвращением.",
  signInSub: "Войдите, и я подниму то, на чём мы остановились.",
  signInCta: "Продолжить",
  signInSwitch: "Уже есть аккаунт? Войти",
  signUpTitle: "Заведите аккаунт, чтобы в следующий раз я вас помнил.",
  signUpSub: "Почта и пароль. Больше ничего, и карта не нужна.",
  signUpCta: "Создать аккаунт",
  signUpSwitch: "Вы впервые? Создать аккаунт",
  emailLabel: "Почта",
  passwordLabel: "Пароль",
  passwordRule: "Минимум 8 символов.",
  passwordShow: "Показать пароль",
  passwordHide: "Скрыть пароль",
  authBack: "Назад",
  errEmailRequired: "Введите адрес почты.",
  errEmailInvalid: "Это не похоже на адрес почты.",
  errPasswordRequired: "Введите пароль.",
  errPasswordShort: "В пароле нужно минимум 8 символов.",
  signOutCta: "Выйти",
  signOut: "Выходим. Записи остаются.",
  ofN: (a, b) => `${a} из ${b}`,

  studiesLabel: "Исследования",
  studiesCount: (n) =>
    `${n} ${pluralRu(n, "исследование", "исследования", "исследований")}`,
  studiesTitle: "Спросите других.",
  studiesSub: "Один вопрос, несколько человек, один ответ, который можно защитить.",
  studiesEmpty: "Исследований пока нет. Сформулируйте вопрос — получите ссылку.",
  studiesBack: "Назад к разговору",
  studiesListFailed: "Список не загрузился. Попробуйте ещё раз.",
  studyNewCta: "Новое исследование",
  studyUntitled: "Без названия",
  studyOpenFailed: "Это исследование не открылось. Остаёмся на прежнем.",

  studyCreateTitle: "На какой вопрос нужен ответ?",
  studyCreateSub: "Четыре решения и план тем. Две минуты — и у вас есть ссылка.",
  studyGoalLabel: "Вопрос",
  studyGoalPlaceholder: "Почему люди перестают платить на третий месяц?",
  studyGoalHint: "Ваш, не их. Эту фразу не увидит ни один собеседник.",
  studyGoalRequired: "Сначала сформулируйте вопрос.",
  studyAudienceLabel: "Кого спрашиваете",
  studyAudience: {
    customer: "Клиентов",
    employee: "Свою команду",
    friendly: "Тех, кто скажет прямо",
  },
  studyAudienceHint: {
    customer: "Разведка. Спрашиваем про их жизнь, а не про вашу идею.",
    employee: "Внутренняя диагностика. Результаты по группам, никогда по одному человеку.",
    friendly: "Проверка гипотезы с теми, кто не станет щадить.",
  },
  studyTimeLabel: "Сколько их времени это стоит",
  studyTimeHint: "Это обещание, а не оценка. Интервью закончится, когда время выйдет.",
  studyMinutes: (n) => `${n} мин`,
  studyTargetLabel: "Сколько человек",
  studyTargetHint:
    "Восьми обычно хватает, чтобы увидеть закономерность. Меньше пяти — это только сигнал.",
  studySpineLabel: "Что должно прозвучать в каждом интервью",
  studySpineHint: "От трёх до шести тем. Не вопросов: порядок меняется по ходу разговора.",
  studySpineDraftCta: "Набросать по вопросу",
  studySpineDrafting: "Набрасываю план",
  studySpineDraftFailed: "Набросок не пришёл. Напишите темы сами или попробуйте ещё раз.",
  studyTopicTitlePlaceholder: "Тема",
  studyTopicIntentPlaceholder: "Что нужно понять по итогу",
  studyTopicAddCta: "Добавить тему",
  studyTopicRemoveCta: "Убрать",
  studyHypothesesLabel: "Во что вы верите сейчас",
  studyHypothesesHint: "Необязательно и полезно. В выжимке будет видно, что из этого выжило.",
  studyHypothesisPlaceholder: "Уходят, потому что запуск занимает неделю",
  studyHypothesisAddCta: "Добавить",
  studyCreateCta: "Создать исследование и ссылку",
  studyCreateFailed: "Исследование не создалось. Попробуйте ещё раз.",
  studyCreating: "Собираю исследование",
  studyCancelCta: "Отмена",

  studyLinkLabel: "Ссылка",
  studyLinkOnce:
    "Показываем один раз. Нигде её не храним. Потеряете — сделаете новую.",
  studyLinkCopyCta: "Скопировать ссылку",
  studyLinkCopied: "Скопировано. Отправьте, пока не закрыли.",
  studyLinkCopyFailed: "Буфер обмена не дался. Выделите ссылку и скопируйте вручную.",
  studyLinkMakeCta: "Сделать ссылку",
  studyLinkNewCta: "Сделать ещё одну",
  studyLinkFailed: "Ссылка не создалась. Попробуйте ещё раз.",
  studyLinkExpires: (when) => `Действует до ${when}`,
  studyLinkNone: "Ссылки пока нет. Сделайте и разошлите.",
  studyLinksLabel: "Сделанные ссылки",
  studyLinkKind: { open: "Открытая ссылка", personal: "Личная ссылка" },
  studyLinkUsed: "использовано",
  studyLinkRevokeCta: "Отозвать",
  studyLinkRevoked: "Отозвана",
  studyLinkExpired: "Истекла",
  studyLinkRevokeFailed: "Ссылку не удалось отозвать. Попробуйте ещё раз.",

  studyGoalHeader: "Вопрос",
  studyStatus: { draft: "Черновик", fielding: "В поле", closed: "Закрыто" },
  studyProgressSub: "завершённых интервью",
  studyMetaAudience: "Спрашиваем",
  studyMetaTime: "На каждого",
  studyParticipantsLabel: "Кто ответил",
  studyParticipantsEmpty: "Пока никого. Доска заполняется по мере ответов.",
  studyAnonymous: "Аноним",
  participantStatus: {
    invited: "Приглашён",
    screened_out: "Не подошёл",
    over_quota: "Группа набрана",
    started: "Говорит",
    completed: "Закончил",
    abandoned: "Прервал",
    disqualified: "Исключён",
  },
  studyTurnCount: (n) => `${n} ${pluralRu(n, "реплика", "реплики", "реплик")}`,
  studyDuration: (ms) => `${Math.max(1, Math.round(ms / 60000))} мин`,
  studyDurationShort: "меньше минуты",
  studyReadCta: "Читать",
  studyHideCta: "Свернуть",
  studyTranscriptFailed: "Расшифровка не открылась. Попробуйте ещё раз.",
  studyCloseCta: "Закрыть исследование",
  studyUpdateFailed: "Не сохранилось. Попробуйте ещё раз.",
  studyReady: {
    too_few: "Слишком мало, чтобы говорить о закономерности. Рассылайте ссылку дальше.",
    enough: "Уже есть смысл считать.",
    saturating: "Последние интервью почти ничего не добавляют. Это близко к концу.",
    closed: "Закрыто. Больше ничего не придёт.",
  },

  synthesisLabel: "Выжимка",
  synthesisNone: "Выжимки пока нет.",
  synthesisRunCta: "Свести",
  synthesisRunAgainCta: "Пересчитать",
  synthesisCost:
    "Прогон читает каждое интервью и стоит денег, поэтому запускается только по вашей команде.",
  synthesisRunning: (done, total) => `Читаю интервью ${done} из ${total}`,
  synthesisRunningVague: "Читаю интервью",
  synthesisFailed: "Прогон не дошёл до конца. Ничего не потеряно. Попробуйте ещё раз.",
  synthesisFrom: (n, when) => `${n} ${pluralRu(n, "интервью", "интервью", "интервью")}, ${when}`,
  synthesisAnswerLabel: "Ответ",
  synthesisThemesLabel: "Темы",
  synthesisAskedOf: "из тех, с кем эта тема вообще всплыла",
  synthesisBand: { strong: "Твёрдо", provisional: "Предварительно", signal: "Сигнал" },
  synthesisDirectness: {
    behaviour: "Поведение",
    opinion: "Мнение",
    hypothetical: "Предположение",
  },
  synthesisPrimed: "Слово подсказал интервьюер",
  synthesisAgainst: "Против",
  synthesisEvidenceCta: "Слова",
  synthesisEvidenceHideCta: "Скрыть слова",
  synthesisSaidDidLabel: "Сказал / сделал",
  synthesisSaidDidHint: "Один и тот же человек обещает одно, а сделал уже другое.",
  synthesisSaid: "Сказал",
  synthesisDid: "Сделал",
  synthesisDisagreementsLabel: "В чём расходятся",
  synthesisDisagreementsHint:
    "Оставлено как есть. Работа именно в расхождении, поэтому мы его не усредняем.",
  synthesisFor: "За",
  synthesisWithheld: (n) =>
    `${n} ${pluralRu(n, "тема не показана", "темы не показаны", "тем не показано")}: слишком мало людей, чтобы отчитываться по группе.`,
  synthesisUncountedLabel: "Наши слова, не их",
  synthesisUncountedHint:
    "Больше половины того, на чём это держится, — ответ на слово, которое первым " +
    "произнёс интервьюер. Считать такое значило бы считать, как часто задавали " +
    "вопрос, поэтому в ответ это не идёт и показано здесь целиком.",
  synthesisUncountedMark: "Слово интервьюера",
  synthesisSinglesLabel: "Это сказал один человек",
  synthesisSinglesHint:
    "Стоит знать. В счёт не идёт: один человек — это не закономерность.",
  synthesisHypothesesLabel: "Во что вы верили на входе",
  synthesisVerdict: {
    supported: "Подтвердилось",
    contradicted: "Опровергнуто",
    untested: "Не проверено",
    refined: "Превратилось в другое",
  },
  synthesisThreshold: (n) =>
    `Только по группам. Того, что сказали меньше ${n} человек, здесь не будет.`,
  synthesisSaturationLabel: "Насыщение",
  synthesisSaturationEarly: (n) => `${n} интервью. Рано судить. Всё выше — только сигнал.`,
  synthesisSaturationAt: (n, pct) =>
    `Три интервью до ${n}-го добавили ${pct}% новых кодов. Это принятая точка насыщения. Ещё два сделали бы её твёрдой.`,
  synthesisSaturationFirm: (n) =>
    `К ${n}-му интервью три подряд не добавили ничего нового. Твёрже этот метод не бывает.`,
  synthesisSaturationNone: "Новое приходит с каждым интервью. Продолжайте.",
  synthesisNextLabel: "О чём спросить дальше",
  synthesisArtifactsLabel: "Забрать с собой",
  synthesisArtifact: {
    brief: "Выжимка",
    docx: "Выжимка документом",
    csv: "Все цитаты таблицей",
    book: "То же самое книгой Excel",
    page: "Страница, которую можно отправить",
  },
  synthesisArtifactsSub:
    "В каждом утверждении сказано, сколько человек это произнесли и скольких спрашивали.",


  deleteCta: "Удалить",
  deleteConfirm: "Удалить этот разговор? Записи уйдут вместе с ним.",
  deleteYes: "Удалить",
  deleteNo: "Оставить",
  deleteFailed: "Не удалилось. Попробуйте ещё раз.",
};

/** Russian counts in three forms; 11 to 14 are the exception that catches everyone. */
function pluralRu(n: number, one: string, few: string, many: string): string {
  const abs = Math.abs(n) % 100;
  if (abs >= 11 && abs <= 14) return many;
  const last = abs % 10;
  if (last === 1) return one;
  if (last >= 2 && last <= 4) return few;
  return many;
}

/**
 * When something last happened, in words.
 *
 * The product's promise is that it remembers across sessions, and until this existed
 * it rendered no timestamp anywhere — so the line under the blob could have been said
 * five minutes or five weeks ago with no way to tell. Relative rather than absolute:
 * "three days ago" is what a person wants; "2026-08-25 14:02" is what a database
 * wants. `Intl` carries both languages, so this needs no strings of its own.
 */
export function formatSince(iso: string, locale: Locale): string {
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return "";
  const seconds = Math.round((then - Date.now()) / 1000);
  const format = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
  const steps: [Intl.RelativeTimeFormatUnit, number][] = [
    ["second", 60],
    ["minute", 60],
    ["hour", 24],
    ["day", 7],
    ["week", 4.348],
    ["month", 12],
  ];
  let value = seconds;
  for (const [unit, size] of steps) {
    if (Math.abs(value) < size) return format.format(Math.round(value), unit);
    value /= size;
  }
  return format.format(Math.round(value), "year");
}

/** Keyed by Locale, so a missing Russian key fails the build rather than the user. */
export const COPY: Record<Locale, Copy> = { en, ru };

export function useCopy(locale: Locale): Copy {
  return COPY[locale];
}
